import { useMemo } from 'react';
import type { ResolvedStackBlock, BlockRendererSharedProps, RenderBlockFn, ViewBlockDataSource } from '../types/blocks';
import { interpolateBlockParams } from '../registry/interpolateBlockParams';

export interface StackBlockRendererProps extends BlockRendererSharedProps {
  block: ResolvedStackBlock;
  renderBlock: RenderBlockFn;
}

const ALIGN_MAP: Record<string, string> = {
  start: 'flex-start',
  end: 'flex-end',
  between: 'space-between',
  center: 'center',
};

export function StackBlockRenderer({
  block,
  renderBlock,
  params,
  meta,
  onSuccess,
  onError,
  onCancel,
  inheritedDataSource,
}: StackBlockRendererProps): JSX.Element {

  const downstreamDataSource = useMemo<ViewBlockDataSource | undefined>(() => {
    if (block.fetchActionId) {
      return {
        fetch: {
          actionId: block.fetchActionId,
          params: { id: params?.id ?? '' },
        },
      };
    }

    const rawDataSource = block.dataSource ?? inheritedDataSource;
    if (!rawDataSource?.fetch?.params) return rawDataSource;
    return {
      ...rawDataSource,
      fetch: {
        ...rawDataSource.fetch,
        params: interpolateBlockParams(rawDataSource.fetch.params, params, meta),
      },
    };
  }, [block.fetchActionId, block.dataSource, inheritedDataSource, params, meta]);

  const shared: BlockRendererSharedProps = {
    params,
    meta,
    onSuccess,
    onError,
    onCancel,
    inheritedDataSource: downstreamDataSource,
  };

  const direction = block.direction ?? 'column';
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: direction,
    gap: block.gap ?? 0,
    ...(block.align != null ? { justifyContent: ALIGN_MAP[block.align] ?? block.align } : {}),
    ...(direction === 'row' ? { alignItems: 'center' } : {}),
    ...(block.wrap === true ? { flexWrap: 'wrap' } : {}),
    ...(block.sticky === true ? { position: 'sticky', bottom: 0, zIndex: 10, background: 'var(--ui-bg, #fff)' } : {}),
    ...(block.divider === true ? { borderTop: '1px solid var(--ui-border-muted, #e5e7eb)', paddingTop: 16 } : {}),
  };

  return (
    <div data-semantic-id={block.semanticId} style={containerStyle}>
      {block.blocks.map((child, i) => (
        <div key={`${child.$id ?? child.$type}-${i}`}>{renderBlock(child, shared)}</div>
      ))}
    </div>
  );
}
